import { Box, chakra, Flex, HStack, Spacer, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import * as Sentry from '@sentry/react';
import { useLocation, useRouterState } from '@tanstack/react-router';
import MessageSquarePlusIcon from '~icons/lucide/message-square-plus';
import { Suspense, useRef } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { tracking } from '#features/analytics';
import { OfflineBanner } from '#features/network/components/OfflineBanner';
import { Link } from '#features/routing';
import { Button } from '#features/ui';
import { IconButton } from '#features/ui/components/IconButton';
import { envConfig } from '#constants/envConfig';
import { useSSR } from '#hooks/useSSR';
import { lazyNamedWithRetry } from '#utils/lazyWithRetry';
import { useMobileHeader } from '../../hooks/useMobileHeader';
import { useHeaderHeightVar } from '../../utils/headerHeight';
import { homeRoute } from '../../utils/homeRoute';
import { UserSettings } from '../UserSettings';
import { Logo } from './Logo';
import { MobileScreenHeader } from './MobileScreenHeader';

const DynamicNotificationsPopover = lazyNamedWithRetry(
  () => import('#features/notifications/components/NotificationsPopover'),
  'NotificationsPopover'
);

// Заглушка держит место колокольчика, пока он грузится, и остаётся вместо него, если чанк так и
// не приехал (блокировщик, оборванная сеть): без своей границы такая ошибка всплывает до
// роутового boundary и вместо страницы показывается экран ошибки.
const notificationsPlaceholder = <Box h="40px" w="40px" />;

export const Header = () => {
  const { isBrowser } = useSSR();
  const isAuth = useIsAuth();
  const mobile = useMobileHeader(isAuth);
  const isLanding = useRouterState({
    select: (state) => state.matches?.some((match) => match?.staticData?.rendersOwnFooter) ?? false,
  });
  return <HeaderView isBrowser={isBrowser} isAuth={isAuth} isLanding={isLanding} mobile={mobile} />;
};

/** Shared presentation also used by the isolated RU/EN responsive preview. */
export const HeaderView = ({
  isBrowser,
  isAuth,
  isLanding,
  mobile,
}: {
  isBrowser: boolean;
  isAuth: boolean;
  isLanding: boolean;
  mobile: ReturnType<typeof useMobileHeader>;
}) => {
  const { t } = useLingui();
  const { pathname } = useLocation();
  const contextual = mobile.screen.kind !== 'brand';
  const isTask = mobile.screen.kind === 'task';
  const hasBack = ['detail', 'settings', 'task'].includes(mobile.screen.kind);
  const showMarketingNav = isBrowser && !isAuth && isLanding;
  const headerRef = useRef<HTMLDivElement>(null);
  useHeaderHeightVar(headerRef);

  return (
    <Flex
      as="header"
      ref={headerRef}
      // Grows by the notch inset so the bar's own background fills the status-bar strip
      // and the logo/actions still sit in the 60px below it. Resolves to plain 60px
      // everywhere the inset is 0 — mobile web, desktop and the Android shell — so this
      // is a no-op outside the iOS app, which is the only place the webview draws
      // edge-to-edge (see useNativeViewportFit).
      //
      // Высота - минимальная, а не точная: под рядом шапки живёт полоса "Нет подключения", и
      // пока сети нет, шапка вырастает на её высоту и сдвигает страницу, а не накрывает её.
      // Настоящую высоту для якорей шапка публикует сама (HEADER_HEIGHT в utils/headerHeight).
      minH="calc(60px + env(safe-area-inset-top, 0px))"
      pt="env(safe-area-inset-top, 0px)"
      flexDir="column"
      w="100%"
      pos="sticky"
      top="0"
      left="0"
      zIndex="sticky"
      position="sticky"
      bg={{ base: 'bg.glass', md: 'bg' }}
      backdropFilter={{ base: 'blur(14px) saturate(130%)', md: 'none' }}
      boxShadow="md"
      css={{
        '@media (max-width: 47.999rem)': { WebkitBackdropFilter: 'blur(14px) saturate(130%)' },
        '@media (max-width: 63.999rem)': {
          '&': {
            '& [data-header-brand]': { minWidth: 0, flex: '1 1 0%' },
            '& [data-header-brand] a': {
              minWidth: 0,
              width: '100%',
              maxWidth: '200px',
              padding: 0,
            },
            '& [data-header-brand] svg': {
              minWidth: 0,
              width: '100%',
              height: 'auto',
              flexShrink: 1,
            },
            '& [data-header-brand] sup': { flexShrink: 0 },
            '& [data-header-spacer]': { display: 'none' },
            '& [data-header-actions]': { flexShrink: 0, gap: 'var(--chakra-spacing-1)' },
            '& [data-header-actions] > a, & [data-header-actions] button': {
              minWidth: '44px',
              minHeight: '44px',
            },
          },
        },
      }}
    >
      <Flex
        maxW="1800px"
        px={{ base: 3, md: 5 }}
        py={2}
        w="100%"
        h="60px"
        flexShrink={0}
        m="0 auto"
      >
        {contextual && <MobileScreenHeader {...mobile} />}
        <Flex
          alignItems="center"
          data-header-brand
          minW={0}
          display={{ base: contextual ? 'none' : 'flex', lg: 'flex' }}
        >
          <Flex px={2} w={{ base: '200px', md: 'auto' }} asChild>
            <Link to={homeRoute(isAuth)}>
              <Logo />
              <Text as="sup" mt={4} textStyle="strong" color="fg.emphasized">
                {envConfig.DEV_BADGE ? 'dev' : 'beta'}
              </Text>
            </Link>
          </Flex>
          {showMarketingNav && (
            <HStack as="nav" gap={6} ml={{ lg: 6, xl: 10 }} display={{ base: 'none', lg: 'flex' }}>
              <chakra.a
                href="#how-it-works"
                textStyle="title"
                color="fg.muted"
                _hover={{ color: 'fg.emphasized' }}
                transition="color var(--chakra-durations-fast)"
              >
                {t`How it works`}
              </chakra.a>
              <Text
                asChild
                textStyle="title"
                color="fg.muted"
                _hover={{ color: 'fg.emphasized' }}
                transition="color var(--chakra-durations-fast)"
              >
                <Link to="/{-$locale}/blog">{t`Blog`}</Link>
              </Text>
              <Text
                asChild
                textStyle="title"
                color="fg.muted"
                _hover={{ color: 'fg.emphasized' }}
                transition="color var(--chakra-durations-fast)"
              >
                <Link to="/{-$locale}/faq">{t`FAQ`}</Link>
              </Text>
            </HStack>
          )}
        </Flex>
        <Spacer data-header-spacer />
        {isBrowser && !isAuth && (
          <HStack gap={2}>
            {isLanding && (
              <Button
                display={{ base: 'none', sm: 'flex' }}
                onClick={() =>
                  tracking.event({
                    action: 'click_sign_up',
                    category: 'auth',
                  })
                }
                asChild
              >
                <Link to="/{-$locale}/sign-up">{t`Find teammates`}</Link>
              </Button>
            )}
            {!['/sign-in'].includes(pathname) && (
              <Button
                display={{ base: 'none', sm: 'flex' }}
                onClick={() =>
                  tracking.event({
                    action: 'click_sign_in',
                    category: 'auth',
                  })
                }
                asChild
                variant="outline"
              >
                <Link to="/{-$locale}/sign-in">{t`common:auth.signIn`}</Link>
              </Button>
            )}
          </HStack>
        )}
        {isBrowser && isAuth ? (
          <HStack
            gap={3}
            data-header-actions
            display={{ base: isTask ? 'none' : 'flex', lg: 'flex' }}
            ml={contextual ? 2 : undefined}
          >
            <Button
              display={{ base: 'none', lg: 'flex' }}
              variant="outline"
              onClick={() =>
                tracking.event({
                  action: 'click_create_post',
                  category: 'posts',
                })
              }
              asChild
            >
              <Link to="/{-$locale}/dashboard/discover/create">{t`Find teammates`}</Link>
            </Button>
            {/* Постоянный вход в обратную связь: одна иконка рядом с колокольчиком вместо
                плавающей кнопки поверх контента — та на телефоне перекрывает ленту и читается
                как чужой виджет. Отсюда видно на любом экране внутри аккаунта. */}
            <IconButton
              aria-label={t`What should we improve?`}
              display={{
                base:
                  mobile.screen.kind === 'detail' && mobile.screen.entity === 'notifications'
                    ? 'none'
                    : 'inline-flex',
                lg: 'inline-flex',
              }}
              title={t`What should we improve?`}
              variant="ghost"
              asChild
            >
              <Link to="/{-$locale}/feedback">
                <MessageSquarePlusIcon />
              </Link>
            </IconButton>
            <Box display={{ base: hasBack ? 'none' : 'block', lg: 'block' }}>
              <Sentry.ErrorBoundary fallback={notificationsPlaceholder}>
                <Suspense fallback={notificationsPlaceholder}>
                  <DynamicNotificationsPopover />
                </Suspense>
              </Sentry.ErrorBoundary>
            </Box>
            <UserSettings />
          </HStack>
        ) : null}
      </Flex>
      <OfflineBanner />
    </Flex>
  );
};
